/**
 * ClienteAuthContext — sessão do Portal Cliente (B1.5).
 *
 * Distinto do AuthContext interno (Google OAuth + Bearer JWT) — Portal
 * Cliente usa email+senha + cookie httpOnly `igm_cliente_session`.
 *
 * Estado gerenciado:
 *   - user: { id, email, nome } | null
 *   - clientes: array de { id, nome, role } vinculados ao user (multi-cliente)
 *   - isLoading: true durante hidratação inicial (fetch /perfil + /meus-clientes)
 *   - isAuthenticated: true quando user definido
 *
 * Hidratação inicial (mount):
 *   - GET /api/v1/cliente/perfil + /meus-clientes via cookie
 *   - Se cookie inválido/ausente → 401 → user=null (sem erro)
 *   - Se cookie válido → popula user + clientes
 *
 * Mutations:
 *   - login(email, senha) → POST /login → atualiza user + clientes
 *   - logout() → POST /logout → limpa estado + cookie
 *   - refreshSession() → re-fetch perfil (após aceitar-convite/redefinir-senha
 *     que fazem auto-login server-side mas frontend precisa atualizar state)
 *
 * NÃO REVERTER:
 *   - ZERO localStorage (Regra inviolável — cookie httpOnly é fonte única)
 *   - Hidratação no mount não bloqueia render (isLoading flag controla)
 *   - 401 na hidratação NÃO é erro — só significa "sem cookie válido"
 *   - logout fire-and-forget no backend mas SEMPRE limpa state local
 *   - FASE-C: a hidratação NÃO roda nas rotas públicas do portal (ver abaixo)
 */

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { clienteApi, ClienteApiError } from '@/lib/cliente-api';

/**
 * Rotas do portal que existem JUSTAMENTE porque ainda não há sessão.
 *
 * O diagnóstico registrou **2 requests 401 no console da tela de login** do
 * cliente: o provider hidratava `/perfil` + `/meus-clientes` no mount de
 * QUALQUER rota `/cliente/*`, inclusive naquelas em que, por definição, não
 * existe cookie de sessão. Resultado: dois 401 vermelhos no console de uma
 * página pública — ruído que parece defeito para quem abre o inspetor.
 *
 * O cookie é httpOnly (o navegador não deixa o JS lê-lo), então a "checagem de
 * token" possível é esta: se a rota é pública, não pergunta. Quem entra por
 * login/convite/redefinição recebe a sessão pela própria resposta do backend
 * (`login()` popula o estado; convite e redefinição chamam `refreshSession()`),
 * então nada deixa de funcionar — só o pedido inútil deixa de sair.
 */
const ROTAS_PUBLICAS_DO_PORTAL = [
  '/cliente/login',
  '/cliente/recuperar-senha',
  '/cliente/redefinir-senha',
  '/cliente/aceitar-convite',
];

function rotaAtualEhPublica(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname;
  return ROTAS_PUBLICAS_DO_PORTAL.some((p) => path === p || path.startsWith(`${p}/`));
}

export interface ClienteUser {
  id: string;
  email: string;
  nome: string;
}

export interface ClienteBinding {
  id: string;
  nome: string;
  role: 'gestor' | 'portaria';
}

interface PerfilResponse {
  user: ClienteUser;
  /** F3: id do super-admin quando a sessão é uma impersonação ("Entrar como"). */
  impersonatedBy?: string | null;
}

interface MeusClientesResponse {
  clientes: ClienteBinding[];
}

interface LoginResponse {
  user: ClienteUser;
  clientes: ClienteBinding[];
}

interface ClienteAuthContextValue {
  user: ClienteUser | null;
  clientes: ClienteBinding[];
  isLoading: boolean;
  isAuthenticated: boolean;
  /** F3: id do super-admin se esta sessão é uma impersonação ("Entrar como"). */
  impersonatedBy: string | null;
  /**
   * Login email+senha. Throws `ClienteApiError` em falha (caller deve tratar
   * com try/catch e mostrar mensagem genérica — backend já envia
   * "credenciais-invalidas" sem revelar se email existe).
   */
  login: (email: string, senha: string) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Re-fetch /perfil + /meus-clientes. Útil após aceitar-convite ou
   * redefinir-senha (que fazem auto-login server-side via Set-Cookie).
   */
  refreshSession: () => Promise<void>;
}

const ClienteAuthContext = createContext<ClienteAuthContextValue | null>(null);

export function ClienteAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<ClienteUser | null>(null);
  const [clientes, setClientes] = useState<ClienteBinding[]>([]);
  const [impersonatedBy, setImpersonatedBy] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPerfilEClientes = useCallback(async () => {
    try {
      const [perfil, meusClientes] = await Promise.all([
        clienteApi.get<PerfilResponse>('/perfil'),
        clienteApi.get<MeusClientesResponse>('/meus-clientes'),
      ]);
      setUser(perfil.user);
      setClientes(meusClientes.clientes);
      setImpersonatedBy(perfil.impersonatedBy ?? null);
    } catch (err) {
      // 401 = sem cookie válido (esperado pra rotas públicas). Limpa state.
      if (err instanceof ClienteApiError && (err.status === 401 || err.status === 403)) {
        setUser(null);
        setClientes([]);
        setImpersonatedBy(null);
      } else {
        // Outros erros (rede, 500) — loga mas não bloqueia
        console.error('[ClienteAuth] fetchPerfilEClientes erro:', err);
        setUser(null);
        setClientes([]);
        setImpersonatedBy(null);
      }
    }
  }, []);

  // Hidratação inicial no mount — pulada nas rotas públicas do portal (não há
  // sessão pra hidratar lá; ver ROTAS_PUBLICAS_DO_PORTAL).
  useEffect(() => {
    if (rotaAtualEhPublica()) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      await fetchPerfilEClientes();
      if (!cancelled) setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchPerfilEClientes]);

  const login = useCallback(async (email: string, senha: string) => {
    const r = await clienteApi.post<LoginResponse>('/login', { email, senha });
    setUser(r.user);
    setClientes(r.clientes);
  }, []);

  const logout = useCallback(async () => {
    try {
      await clienteApi.post('/logout');
    } catch (err) {
      // Logout é best-effort — mesmo se backend falhar, limpa estado local
      console.warn('[ClienteAuth] logout backend erro (ignorado):', err);
    }
    setUser(null);
    setClientes([]);
    setImpersonatedBy(null);
  }, []);

  const refreshSession = useCallback(async () => {
    await fetchPerfilEClientes();
  }, [fetchPerfilEClientes]);

  return (
    <ClienteAuthContext.Provider
      value={{
        user,
        clientes,
        isLoading,
        isAuthenticated: user !== null,
        impersonatedBy,
        login,
        logout,
        refreshSession,
      }}
    >
      {children}
    </ClienteAuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useClienteAuth(): ClienteAuthContextValue {
  const ctx = useContext(ClienteAuthContext);
  if (!ctx) {
    throw new Error('useClienteAuth requer <ClienteAuthProvider> no tree');
  }
  return ctx;
}
